import { Capacitor, Plugins } from "@capacitor/core";
import { GqlUser } from "@penfold/graphql";
import gql from "graphql-tag";
import { analytics } from "lib/analytics";
import { createContext, useContext, useMemo, useState } from "react";
import { useQuery } from "react-apollo";
import { schema } from "shared/analytics";
import {
  nullAppStoreReviewContext,
  requestAppStoreReview,
  ReviewContext,
} from "./app-store-review";

export const AppStoreReviewContext = createContext<ReviewContext>(
  nullAppStoreReviewContext
);

interface AppStoreReviewQuery {
  me: Pick<GqlUser, "isHighReviewer">;
}

export const GET_IS_HIGH_REVIEWER_QUERY = gql`
  query AppStoreReview {
    me {
      id
      isHighReviewer
    }
  }
`;

export const AppStoreReviewProvider = ({ children }) => {
  const isAppStoreReviewAvailable =
    Capacitor.isPluginAvailable("CapacitorRateApp") && Capacitor.isNative;
  const [hasUserDemonstratedEngagement, setHasUserDemonstratedEngagement] =
    useState(false);
  const { data, loading, error } = useQuery<AppStoreReviewQuery>(
    GET_IS_HIGH_REVIEWER_QUERY,
    { skip: !isAppStoreReviewAvailable }
  );

  const value = useMemo(() => {
    if (!isAppStoreReviewAvailable || loading || error) {
      return nullAppStoreReviewContext;
    } else {
      return {
        userDemonstratedEngagement: () => {
          setHasUserDemonstratedEngagement(true);
          analytics.trackEvent(schema.USER_DEMONSTRATED_ENGAGEMENT, {
            from: "top-up payment",
          });
        },
        requestReview: () => {
          void requestAppStoreReview({
            hasUserDemonstratedEngagement,
            isHighReviewer: data?.me.isHighReviewer,
            keyValueStore: Plugins.Storage,
            rateRequest: Plugins.CapacitorRateApp.requestReview,
          });
          setHasUserDemonstratedEngagement(false);
        },
      };
    }
  }, [
    data,
    hasUserDemonstratedEngagement,
    error,
    loading,
    isAppStoreReviewAvailable,
  ]);

  return (
    <AppStoreReviewContext.Provider value={value}>
      {children}
    </AppStoreReviewContext.Provider>
  );
};

export const useAppStoreReview = () => useContext(AppStoreReviewContext);
